import React, { MouseEvent, MouseEventHandler, ReactElement, useCallback, useMemo } from 'react';

import { SxProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { To } from 'react-router-dom';

import { Spinner } from '@/components/spinners/Spinner';
import { useNavigate } from '@/hooks/useNavigate';
import { theme } from '@/theme';
import { stringOfTo } from '@/utils/navigation';
import { renderElement } from '@/utils/react-elements';

import {
    ButtonSize,
    ButtonType,
    ButtonWidth,
    DEFAULT_BUTTON_COLORS,
    DEFAULT_ICON_SIZES,
    DEFAULT_ICON_STYLES_SIZES,
    DEFAULT_SPINNER_SIZES,
} from './Button.consts';
import { ButtonStyled, StyledButtonTitle } from './Button.styles';

export interface ButtonBaseProps {
    type?: ButtonType;
    title?: string;
    size?: ButtonSize;
    width?: ButtonWidth;
    icon?: ReactElement;
    iconPosition?: 'left' | 'right';
    spinnerSize?: number;
    customColor?: string;
    disabled?: boolean;
    isLoading?: boolean;
    sx?: SxProps;
    'aria-label'?: string;
    styles?: {
        loader?: SxProps;
    };
}

export interface ButtonClickProps extends ButtonBaseProps {
    onClick?: MouseEventHandler;
    href?: never;
}

export interface ButtonHrefProps extends ButtonBaseProps {
    onClick?: never;
    href?: To;
}

export type ButtonProps = ButtonClickProps | ButtonHrefProps;

export const Button = ({
    type = 'primary',
    title,
    size = 'normal',
    width = 'default',
    icon,
    iconPosition = 'left',
    spinnerSize,
    customColor,
    disabled,
    isLoading,
    onClick,
    href,
    sx,
    styles,
    ...props
}: ButtonProps) => {
    const { t } = useTranslation();
    const { browserNavigate } = useNavigate();

    const handleClick = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            if (isLoading || disabled) return;

            href ? browserNavigate({ event, to: href }) : onClick?.(event);
        },
        [isLoading, disabled, onClick, href, browserNavigate],
    );

    const hrefProps = useMemo(() => (href ? { href: stringOfTo(href) } : undefined), [href]);

    //

    const buttonColors = useMemo(
        () =>
            type === 'text-custom'
                ? {
                      backgroundColor: 'transparent',
                      borderColor: 'transparent',
                      color: customColor ?? theme.palette.components.button,
                      svg: { color: customColor ?? theme.palette.components.button },
                      ':hover': {
                          backgroundColor: 'transparent',
                          borderColor: 'transparent',
                          color: customColor ?? theme.palette.components.button,
                      },
                  }
                : DEFAULT_BUTTON_COLORS[type],
        [type, customColor],
    );

    const iconComponent = useMemo(
        () =>
            icon
                ? renderElement(icon, {
                      defs: { size: DEFAULT_ICON_SIZES[size], style: { ...DEFAULT_ICON_STYLES_SIZES[size] } },
                  })
                : null,
        [icon, size],
    );

    return (
        <ButtonStyled
            {...hrefProps}
            buttonsize={size}
            buttonwidth={width}
            buttontype={type}
            buttoncolors={buttonColors}
            sx={sx}
            disabled={disabled}
            disableElevation
            onClick={handleClick}
            aria-label={isLoading ? t('loading') : props?.['aria-label']}
        >
            {isLoading ? (
                <Spinner size={spinnerSize ?? DEFAULT_SPINNER_SIZES[size]} sx={styles?.loader} />
            ) : (
                <>
                    {iconPosition === 'left' ? iconComponent : null}
                    {title ? <StyledButtonTitle size={size}>{title}</StyledButtonTitle> : null}
                    {iconPosition === 'right' ? iconComponent : null}
                </>
            )}
        </ButtonStyled>
    );
};
